import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Calendar,
  CheckCircle,
  MoreHorizontal,
  XCircle,
  Video,
  Briefcase,
} from 'lucide-react';
import { authService } from '../../services/auth/authService';
import { DashboardHeader } from '../../components/layout/DashboardHeader/DashboardHeader';
import { DashboardSidebar } from '../../components/layout/DashboardSidebar/DashboardSidebar';
import { createSidebarItems } from '../../components/layout/DashboardSidebar/sidebarItems';
import {
  dashboardService,
  DashboardData,
} from '../../services/dashboard/dashboardService';
import { videoConferenceService, CanJoinCallResponse } from '../../services/video-conference/videoConferenceService';
import { aiRequestsService } from '../../services/ai-requests/aiRequestsService';
import { getConsultationTypeLabel, isOnlineConsultation } from '../../utils/appointmentUtils';
import { formatAppointmentTime } from '../../utils/dateUtils';
import { getInitials } from '../../utils/userUtils';
import { CalendarDatePicker } from '../../components/CalendarDatePicker/CalendarDatePicker';
import './Dashboard.css';

const STORAGE_KEY_DATE_RANGE = 'dashboard_dateRange';

// Helper function to get current month date range
const getCurrentMonthRange = () => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    startDate: startOfMonth.toISOString().split('T')[0],
    endDate: endOfMonth.toISOString().split('T')[0],
  };
};

const loadStoredDateRange = (): { startDate: string; endDate: string } => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_DATE_RANGE);
    if (!raw) return getCurrentMonthRange();
    const parsed = JSON.parse(raw) as { startDate?: string; endDate?: string };
    const startDate = parsed?.startDate && /^\d{4}-\d{2}-\d{2}$/.test(parsed.startDate) ? parsed.startDate : null;
    const endDate = parsed?.endDate && /^\d{4}-\d{2}-\d{2}$/.test(parsed.endDate) ? parsed.endDate : null;
    if (startDate && endDate) return { startDate, endDate };
  } catch {
    // ignore
  }
  return getCurrentMonthRange();
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState(loadStoredDateRange);
  const [canJoinStatuses, setCanJoinStatuses] = useState<Map<number, CanJoinCallResponse>>(new Map());
  const [loadingCanJoin, setLoadingCanJoin] = useState<Set<number>>(new Set());
  const [processingRequestId, setProcessingRequestId] = useState<number | null>(null);

  // Load can-join status for an appointment from backend
  const loadCanJoinStatus = useCallback(async (appointmentId: number, forceRefresh = false) => {
    if (loadingCanJoin.has(appointmentId)) {
      return; // Already loading
    }
    if (!forceRefresh && canJoinStatuses.has(appointmentId)) {
      return; // Already loaded (unless forcing refresh)
    }
    
    try {
      setLoadingCanJoin(prev => new Set(prev).add(appointmentId));
      const response = await videoConferenceService.canJoinCallForAppointment(appointmentId);
      setCanJoinStatuses(prev => new Map(prev).set(appointmentId, response));
    } catch (error) {
      console.error(`Failed to load can-join status for appointment ${appointmentId}:`, error);
      // On error, default to not allowing join
      setCanJoinStatuses(prev => new Map(prev).set(appointmentId, { can_join: false, message: 'Unable to check join status' }));
    } finally {
      setLoadingCanJoin(prev => {
        const next = new Set(prev);
        next.delete(appointmentId);
        return next;
      });
    }
  }, [loadingCanJoin, canJoinStatuses]);

  // Get can-join status for an appointment (from backend response)
  const canJoinCall = useCallback((appointmentId: number): boolean => {
    const status = canJoinStatuses.get(appointmentId);
    return status?.can_join ?? false;
  }, [canJoinStatuses]);

  // Get join message for an appointment (from backend response)
  const getJoinMessage = useCallback((appointmentId: number): string | undefined => {
    const status = canJoinStatuses.get(appointmentId);
    return status?.message;
  }, [canJoinStatuses]);


  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await dashboardService.getDashboardData(dateRange.startDate, dateRange.endDate);
        setDashboardData(data);
        
        // Load can-join status for all upcoming video call appointments
        if (data.upcomingAppointments) {
          const videoAppointments = data.upcomingAppointments.filter(
            apt => isOnlineConsultation(apt.appointment_type)
          );
          videoAppointments.forEach(apt => {
            loadCanJoinStatus(apt.id);
          });
        }
      } catch (err: any) {
        console.error('Failed to load dashboard data:', err);
        setError(err.response?.data?.detail || err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [dateRange, loadCanJoinStatus]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_DATE_RANGE, JSON.stringify({ startDate: dateRange.startDate, endDate: dateRange.endDate }));
    } catch {
      // ignore
    }
  }, [dateRange]);

  const handleLogout = () => {
    authService.clearTokens();
    navigate('/login');
  };

  const handleGoToProfile = () => {
    navigate('/profile');
  };

  const handleEditAppointment = (appointmentId: number) => {
    navigate(`/appointments/${appointmentId}/edit`);
  };

  const handleAcceptRequest = async (requestId: number) => {
    try {
      setProcessingRequestId(requestId);
      await aiRequestsService.updateRequestStatus(requestId, 'pending');
      // Reload dashboard data to reflect changes
      const data = await dashboardService.getDashboardData(dateRange.startDate, dateRange.endDate);
      setDashboardData(data);
    } catch (err: any) {
      console.error('Failed to accept request:', err);
      alert(err.response?.data?.detail || 'Failed to accept request');
    } finally {
      setProcessingRequestId(null);
    }
  };

  const sidebarItems = createSidebarItems(navigate, location.pathname);

  // Prepare performance metrics from dashboard data (Lucide icons)
  const totalAppointments = dashboardData?.metrics.totalAppointments ?? 0;
  const completed = dashboardData?.metrics.completed ?? 0;
  const aiPendingCount = dashboardData?.aiRequests.length ?? 0;
  const cancelled = dashboardData?.metrics.cancelled ?? 0;

  const performanceMetrics = [
    {
      label: 'TOTAL VISITS',
      value: totalAppointments.toString(),
      icon: <Calendar size={24} strokeWidth={2} />,
      color: '#f97316',
    },
    {
      label: 'COMPLETED',
      value: completed.toString(),
      icon: <CheckCircle size={24} strokeWidth={2} />,
      color: '#10b981',
    },
    {
      label: 'AI PENDING',
      value: aiPendingCount.toString(),
      icon: <MoreHorizontal size={24} strokeWidth={2} />,
      color: '#6b7280',
    },
    {
      label: 'CANCELLED',
      value: cancelled.toString(),
      icon: <XCircle size={24} strokeWidth={2} />,
      color: '#ef4444',
    },
  ];

  return (
    <div className="dashboard">
      <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />

      <div className="dashboard-layout">
        <DashboardSidebar items={sidebarItems} />

        {/* Main Content Container */}
        <div className="dashboard-container">
          <main className="dashboard-main-content">
            {loading && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                Loading dashboard data...
              </div>
            )}

            {error && (
              <div style={{ padding: '20px', background: '#fee2e2', borderRadius: '8px', color: '#b91c1c', marginBottom: '24px' }}>
                Error: {error}
              </div>
            )}

            {!loading && !error && dashboardData && (
              <div className="dashboard-one-card">
                {/* Section 1: Live Practice Analytics – header and calendar match Patient Insights */}
                <section className="dashboard-card-section analytics-section">
                  <div className="analytics-section-header">
                    <h2 className="analytics-section-title">Live Practice Analytics</h2>
                    <div className="analytics-date-range-box">
                      <div className="analytics-date-range-from">
                        <CalendarDatePicker
                          label="FROM"
                          id="start-date"
                          value={dateRange.startDate}
                          onChange={(startDate) => setDateRange((prev) => ({
                            ...prev,
                            startDate: startDate || prev.endDate,
                          }))}
                          maxDate={dateRange.endDate}
                          placeholder="Select date"
                          dateDisplayFormat="short"
                        />
                      </div>
                      <div className="analytics-date-range-divider" aria-hidden />
                      <div className="analytics-date-range-to">
                        <CalendarDatePicker
                          label="TO"
                          id="end-date"
                          value={dateRange.endDate}
                          onChange={(endDate) => setDateRange((prev) => ({
                            ...prev,
                            endDate: endDate || prev.startDate,
                          }))}
                          minDate={dateRange.startDate}
                          placeholder="Select date"
                          dateDisplayFormat="short"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="performance-cards">
                    {performanceMetrics.map((metric, index) => (
                      <div key={index} className="performance-card">
                        <div className="performance-card-icon" style={{ color: metric.color }}>
                          {metric.icon}
                        </div>
                        <div className="performance-card-content">
                          <div className="performance-card-label">{metric.label}</div>
                          <div className="performance-card-value">{metric.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 2 & 3: Appointments (left) + AI Requests (right) */}
                <div className="dashboard-card-grid">
                  {/* Upcoming Appointments */}
                  <section className="dashboard-card-section appointments-section">
                    <div className="section-header-with-link">
                      <h2 className="section-title section-title-appointments">Upcoming Appointments</h2>
                      <button
                        type="button"
                        className="view-schedule-link"
                        onClick={() => navigate('/appointments')}
                      >
                        FULL CALENDAR
                      </button>
                    </div>
                    {dashboardData.upcomingAppointments.length > 0 ? (
                      <div className="appointments-list">
                        {dashboardData.upcomingAppointments.map((appointment) => {
                          const patientName = appointment.patient_name || 'Unknown Patient';
                          const initials = getInitials(patientName);

                          return (
                            <div key={appointment.id} className="appointment-card">
                              <div className="appointment-avatar">
                                <span>{initials}</span>
                              </div>
                              <div className="appointment-details">
                                <div className="appointment-name">{patientName}</div>
                                <div className="appointment-time-row">
                                  <span className="appointment-time">
                                    {formatAppointmentTime(
                                      appointment.appointment_date,
                                      appointment.duration_minutes ?? appointment.duration
                                    )}
                                  </span>
                                  <span className="appointment-type-icon" title={getConsultationTypeLabel(appointment.appointment_type)}>
                                    {isOnlineConsultation(appointment.appointment_type) ? (
                                      <Video size={16} strokeWidth={2} />
                                    ) : (
                                      <Briefcase size={16} strokeWidth={2} />
                                    )}
                                  </span>
                                </div>
                                <div className="appointment-type">
                                  {getConsultationTypeLabel(appointment.appointment_type)}
                                </div>
                              </div>
                              <div className="appointment-actions">
                                {isOnlineConsultation(appointment.appointment_type) ? (
                                  <>
                                    <button
                                      className={`appointment-action-btn join-button ${
                                        !canJoinCall(appointment.id) ? 'disabled' : ''
                                      }`}
                                      onClick={() => {
                                        if (canJoinCall(appointment.id)) {
                                          navigate(`/video-conference/${appointment.id}`);
                                        }
                                      }}
                                      disabled={!canJoinCall(appointment.id) || loadingCanJoin.has(appointment.id)}
                                      title={!canJoinCall(appointment.id) ? getJoinMessage(appointment.id) : 'Join video call'}
                                    >
                                      {loadingCanJoin.has(appointment.id) ? 'Checking...' : 'JOIN CALL'}
                                    </button>
                                    <button
                                      className="appointment-action-btn edit-button"
                                      onClick={() => handleEditAppointment(appointment.id)}
                                      title="Edit appointment"
                                    >
                                      Edit
                                    </button>
                                  </>
                                ) : (
                                  <>
<button
                                    className="appointment-action-btn view-button"
                                    onClick={() => navigate(`/appointments/${appointment.id}`)}
                                  >
                                    DETAILS
                                  </button>
                                    <button
                                      className="appointment-action-btn edit-button"
                                      onClick={() => handleEditAppointment(appointment.id)}
                                      title="Edit appointment"
                                    >
                                      Edit
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                        No upcoming appointments
                      </div>
                    )}
                  </section>

                  {/* AI Requests */}
                  <section className="dashboard-card-section ai-requests-section">
                    <div className="section-header-with-link">
                      <h2 className="section-title section-title-ai">AI Requests</h2>
                      <button
                        type="button"
                        className="ai-requests-inbox-link"
                        onClick={() => navigate('/ai-requests')}
                      >
                        INBOX
                      </button>
                    </div>
                    {dashboardData.aiRequests.length > 0 ? (
                      <>
                        <div className="ai-requests-list">
                          {dashboardData.aiRequests.map((request) => {
                            const getPatientId = () => {
                              if (request.context) {
                                try {
                                  const context = JSON.parse(request.context);
                                  return context.patient_id ?? request.id;
                                } catch {
                                  return request.id;
                                }
                              }
                              return request.id;
                            };
                            const getPatientInitial = () => {
                              if (request.context) {
                                try {
                                  const context = JSON.parse(request.context);
                                  const name = context.patient_name || '';
                                  return name ? name.charAt(0).toUpperCase() : 'P';
                                } catch {
                                  return 'P';
                                }
                              }
                              return 'P';
                            };
                            const description = request.user_question || 'Consultation request';
                            const isRequested = request.status?.toLowerCase() === 'requested';

                            return (
                              <div key={request.id} className="ai-request-card-v2">
                                <div className="ai-request-card-v2-top">
                                  <div className="ai-request-avatar-v2">
                                    <span>{getPatientInitial()}</span>
                                  </div>
                                  <div className="ai-request-meta">
                                    <span className="ai-request-patient-id">Patient #{getPatientId()}</span>
                                    {isRequested && (
                                      <span className="ai-request-urgent-tag">URGENT</span>
                                    )}
                                  </div>
                                </div>
                                <p className="ai-request-description">{description}</p>
                                <div className="ai-request-actions-v2">
                                  {isRequested && (
                                    <button
                                      type="button"
                                      className="ai-request-btn-accept"
                                      onClick={() => handleAcceptRequest(request.id)}
                                      disabled={processingRequestId === request.id}
                                    >
                                      {processingRequestId === request.id ? 'Accepting...' : 'Accept'}
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="ai-request-btn-details"
                                    onClick={() => navigate(`/ai-requests/${request.id}`)}
                                  >
                                    Details
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <p className="ai-requests-empty-msg">No pending AI requests</p>
                    )}
                  </section>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
